import React, { useContext, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { BusinessAutomationActionTypeEnum } from '@reach/shared-types/business-automation';
import type { AutomationTemplateContents } from '@reach/shared-types/automation-template';
import { AutomationEditorContext } from '../AutomationEditorContext';
import { DialogBackdrop } from '../../../../components/ui/dialog-backdrop';
import { StepDialogFooter } from '../StepDialogFooter';
import { SendMessageForm } from './SendMessageForm';
import { StripoDialog } from './stripo';
import styles from './SendMessageStep.module.css';
import { partnerApi } from '@/lib/api';

type SendMessageAction = Extract<
  AutomationTemplateContents['actionData'][0],
  { type: BusinessAutomationActionTypeEnum.SEND_COMMUNICATION }
>;

type SendMessageFormField =
  | 'subject'
  | 'previewText'
  | 'html'
  | 'css'
  | 'compiledHtml'
  | 'text';

interface SendMessageDialogProps {
  open: boolean;
  onClose: () => void;
  action: SendMessageAction;
  actionIndex: number;
}

export const SendMessageDialog: React.FC<SendMessageDialogProps> = ({
  open,
  onClose,
  action,
  actionIndex,
}) => {
  const { editor } = useContext(AutomationEditorContext)!;
  const { updateAction } = editor;

  const [formData, setFormData] = useState({
    subject: action.subject || '',
    previewText: action.previewText || '',
    html: action.html || '',
    css: action.css || '',
    compiledHtml: action.compiledHtml || '',
    text: action.text || '',
  });

  const { data: partner } = useQuery({
    queryKey: ['partner'],
    queryFn: () => partnerApi.getPartner(),
  });

  const effectiveHtml =
    formData.html || partner?.emailTemplateOverride?.html || '';
  const effectiveCss =
    formData.css || partner?.emailTemplateOverride?.css || '';

  const [stripoIsOpen, setShowStripo] = useState(false);

  // Reset form data when dialog opens or action changes
  useEffect(() => {
    if (open) {
      setFormData({
        subject: action.subject || '',
        previewText: action.previewText || '',
        html: action.html || '',
        css: action.css || '',
        compiledHtml: action.compiledHtml || '',
        text: action.text || '',
      });
    }
  }, [open, action]);

  if (!open) return null;

  const handleOK = () => {
    updateAction(actionIndex, {
      subject: formData.subject,
      previewText: formData.previewText,
      html: formData.html,
      css: formData.css,
      compiledHtml: formData.compiledHtml,
      text: formData.text,
    });
    onClose();
  };

  const setFormFieldValue = (field: SendMessageFormField, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const showStripo = () => setShowStripo(true);
  const hideStripo = () => setShowStripo(false);

  const handleStripoSave = (
    newHtml: string,
    newCss: string,
    compiledHtml: string,
  ) => {
    setFormData(prev => ({
      ...prev,
      html: newHtml,
      css: newCss,
      compiledHtml,
    }));
    setShowStripo(false);
  };

  return (
    <>
      <DialogBackdrop onClose={onClose} />
      <dialog className={styles.dialog} open>
        <header>
          <h2>Configure Send Message</h2>
          <p>Compose your message content</p>
          <button onClick={onClose}>
            <X />
          </button>
        </header>

        <main>
          <SendMessageForm
            subject={formData.subject}
            previewText={formData.previewText}
            html={effectiveHtml}
            css={effectiveCss}
            text={formData.text}
            onChange={setFormFieldValue}
            onOpenStripoEditor={showStripo}
          />
        </main>

        <StepDialogFooter onCancel={onClose} onOK={handleOK} />
      </dialog>

      <StripoDialog
        open={stripoIsOpen}
        onClose={hideStripo}
        html={effectiveHtml}
        css={effectiveCss}
        onSave={handleStripoSave}
      />
    </>
  );
};
